import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    CssBaseline,
    Paper,
    Step as MuiStep,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';

import { leaveDashboardFor } from '../auth/leaveDashboard';
import { handoffRedirectTarget } from '../auth/oauthHandoff';
import config from '../config';
import { createSignupApi, SignupApiError } from './api';
import { SignupContext, SignupContextValue } from './context';
import { ContinueElsewhereDialog, Released } from './ContinueElsewhere';
import { flowReducer, initialFlow, Step, WIZARD_STEPS } from './flow';
import { errorText } from './labels';
import Confirm from './screens/Confirm';
import Connect from './screens/Connect';
import CreateAccount from './screens/CreateAccount';
import Landing from './screens/Landing';
import PickAccount from './screens/PickAccount';
import VerifyEmail from './screens/VerifyEmail';
import {
    clearClaimSecret,
    clearConnectReturn,
    clearStashedCode,
    publicIdOf,
    readClaimSecret,
    pickConnectReturn,
    readConnectReturns,
    readStashedCode,
    stashCodeFromUrl,
    stashConnectReturnFromUrl,
    storeClaimSecret,
} from './storage';
import type { Created, SignupPreview } from './types';

// The self-signup app (backend plan 2026-10-17-dashboard-self-signup-invite-links §16): rendered by
// App.js before the auth gate when the host's first label is `signup`. A minimal shell — the theme
// from index.js, no data provider, no router (only `/` is used) — around the flow in flow.ts.

const CLAIM_REFUSALS: Record<string, 'claimed_elsewhere' | 'used' | 'revoked' | 'expired'> = {
    claimed_elsewhere: 'claimed_elsewhere',
    invite_used: 'used',
    invite_revoked: 'revoked',
    invite_expired: 'expired',
};

const ALLOW_HTTP = config.env === 'dev';

function SignupHeader({ onRelease }: { onRelease: (() => void) | null }) {
    return (
        <Box component="header" sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <Container maxWidth="md" sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 700, flex: 1 }}>TourOptima</Typography>
                {onRelease && (
                    <Button size="small" onClick={onRelease}>Continue on another device</Button>
                )}
                {/* Reserved for the v2 onboarding support chat launcher (plan §17.3). */}
                <Box id="signup-chat-slot" data-testid="signup-chat-slot" />
            </Container>
        </Box>
    );
}

function Progress({ step, bound }: { step: Step; bound: boolean }) {
    const labels = bound ? ['Booking system', 'Company', 'Account'] : ['Booking system', 'Company', 'E-mail', 'Account'];
    const index: Record<string, number> = bound
        ? { connect: 0, pick: 0, confirm: 1, create: 2 }
        : { connect: 0, pick: 0, confirm: 1, verify: 2, create: 3 };
    return (
        <Stepper activeStep={index[step] ?? 0} alternativeLabel sx={{ mb: 3 }}>
            {labels.map((label) => (
                <MuiStep key={label}><StepLabel>{label}</StepLabel></MuiStep>
            ))}
        </Stepper>
    );
}

function Working({ text }: { text: string }) {
    return (
        <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress size={24} />
            <Typography>{text}</Typography>
        </Box>
    );
}

function BootFailure({ error, onRetry }: { error: SignupApiError | null; onRetry: () => void }) {
    const code = error ? error.code : 'server_error';
    const final = ['invalid_code', 'signup_disabled', 'disabled', 'signup_secret_missing'].includes(code);
    return (
        <Box>
            <Typography variant="h5" component="h1" gutterBottom>
                {code === 'invalid_code' ? 'This link is not valid' : 'We cannot open your invitation'}
            </Typography>
            <Alert severity={final ? 'warning' : 'error'} sx={{ my: 2 }}>{errorText(error)}</Alert>
            {!final && <Button variant="contained" onClick={onRetry}>Try again</Button>}
        </Box>
    );
}

export default function SignupApp() {
    // Before anything else renders: the code leaves the address bar for this tab's sessionStorage, and so does what
    // "Connect with <booking system>" came back with (a query string; sentrySetup.js did both already on this host).
    const [{ code, connectReturns }] = React.useState(() => {
        stashCodeFromUrl();
        stashConnectReturnFromUrl();
        return { code: readStashedCode(), connectReturns: readConnectReturns() };
    });
    // Kept in the flow from here on; a reload must not show it again.
    React.useEffect(() => { clearConnectReturn(); }, []);
    const [flow, dispatch] = React.useReducer(flowReducer, null, () => initialFlow(!!code, connectReturns));
    // Back from the booking system, the link this browser holds is resumed without a click (once per page load):
    // a `yours` claim with the stored secret is no new claim, and the client just started it here.
    const resumeAfterConnect = React.useRef(!!code && connectReturns.length > 0);
    const sessionRef = React.useRef<string | null>(null);
    const publicIdRef = React.useRef<string | null>(publicIdOf(code));
    const [claiming, setClaiming] = React.useState(false);
    const [claimError, setClaimError] = React.useState<unknown>(null);
    const [releaseOpen, setReleaseOpen] = React.useState(false);

    const api = React.useMemo(() => {
        const client = createSignupApi({
            getSession: () => sessionRef.current,
            // A session lives 2 h; the stored claim secret gets a fresh one without a click.
            renewSession: async () => {
                const secret = readClaimSecret(publicIdRef.current);
                if (!code || !secret) {
                    return null;
                }
                const claimed = await client.claim(code, secret);
                if (publicIdRef.current) {
                    storeClaimSecret(publicIdRef.current, claimed.claim_secret);
                }
                sessionRef.current = claimed.session;
                return claimed.session;
            },
        });
        return client;
    }, [code]);

    const sessionLost = React.useCallback((error: unknown) => {
        if (!(error instanceof SignupApiError) || !error.isSessionError) {
            return false;
        }
        sessionRef.current = null;
        setReleaseOpen(false);
        dispatch({ type: 'session_lost', notice: errorText(error) });
        return true;
    }, []);

    // The claim: the explicit click (D10), or the resume after "Connect with <booking system>".
    const claimWith = React.useCallback(async (preview: SignupPreview) => {
        if (!code) { return; }
        const publicId = preview.invite.public_id;
        const secret = preview.state === 'yours' ? readClaimSecret(publicId) : null;
        setClaiming(true);
        setClaimError(null);
        try {
            const claimed = await api.claim(code, secret);
            storeClaimSecret(publicId, claimed.claim_secret);
            sessionRef.current = claimed.session;
            dispatch({ type: 'claimed' });
        } catch (error: any) {
            const refused = error && CLAIM_REFUSALS[error.code];
            if (refused) {
                clearClaimSecret(publicId);
                dispatch({ type: 'claim_refused', state: refused, claimed_since: error.claimedSince, sign_in_url: error.signInUrl });
            } else {
                setClaimError(error);
            }
        } finally {
            setClaiming(false);
        }
    }, [api, code]);

    // Boot: preview (opening changes nothing), with this browser's claim secret if it has one.
    React.useEffect(() => {
        if (flow.step !== 'boot' || !code) {
            return undefined;
        }
        let active = true;
        const secret = readClaimSecret(publicIdRef.current);
        api.preview(code, secret).then(
            (preview) => {
                if (!active) { return; }
                publicIdRef.current = preview.invite.public_id;
                if (secret && preview.state !== 'yours') {
                    // Released, taken over, used or revoked since: the secret opens nothing any more.
                    clearClaimSecret(preview.invite.public_id);
                }
                setClaimError(null);
                dispatch({ type: 'previewed', preview });
                if (resumeAfterConnect.current) {
                    resumeAfterConnect.current = false;
                    // Only a return from a booking system this invite may use (any, when it names none).
                    const back = pickConnectReturn(connectReturns, preview.invite.allowed_restechs);
                    if (back && preview.state === 'yours' && secret) {
                        claimWith(preview);
                    } else {
                        // Not a return of ours, or the claim that connected is gone (released, revoked, used).
                        dispatch({ type: 'connect_return_seen' });
                    }
                }
            },
            (error) => {
                if (active) { dispatch({ type: 'preview_failed', failure: error }); }
            },
        );
        return () => { active = false; };
    }, [flow.step, flow.bootSeq, code, api, claimWith, connectReturns]);

    // A link opened in this tab while the page is up changes only the fragment (no reload): start over
    // with its code. (Stripping the fragment uses replaceState, which fires no hashchange.)
    React.useEffect(() => {
        const onHashChange = () => {
            if (!window.location.hash) { return; }
            stashCodeFromUrl();
            window.location.reload();
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    // Done: off to the new dashboard's sign-in (the e-mail prefilled through login_hint).
    React.useEffect(() => {
        if (flow.step !== 'done') {
            return;
        }
        const target = handoffRedirectTarget(flow.signInUrl, { allowHttp: ALLOW_HTTP });
        if (target) {
            leaveDashboardFor(target);
        }
    }, [flow.step, flow.signInUrl]);

    // "Start onboarding" / "Continue": the explicit click that claims (D10).
    const start = () => {
        if (flow.preview) {
            claimWith(flow.preview);
        }
    };

    const release = async () => {
        try {
            await api.release();
        } catch (error) {
            if (sessionLost(error)) { return; }
            throw error;
        }
        clearClaimSecret(publicIdRef.current);
        sessionRef.current = null;
        setReleaseOpen(false);
        dispatch({ type: 'released' });
    };

    const onCreated = (created: Created) => {
        clearClaimSecret(publicIdRef.current);
        clearStashedCode();
        sessionRef.current = null;
        dispatch({ type: 'created', signInUrl: created.sign_in_url });
    };

    const context: SignupContextValue = React.useMemo(
        () => ({ api, flow, dispatch, sessionLost }),
        [api, flow, sessionLost],
    );

    const inWizard = flow.claimed && WIZARD_STEPS.includes(flow.step);
    const bound = !!flow.preview?.invite.bound;

    let content: React.ReactNode;
    switch (flow.step) {
        case 'no_code':
            content = (
                <Box>
                    <Typography variant="h5" component="h1" gutterBottom>Open your invitation link</Typography>
                    <Typography paragraph>
                        This page needs the link from your TourOptima invitation e-mail. Open the link from the e-mail again.
                    </Typography>
                </Box>
            );
            break;
        case 'boot':
            content = <Working text="Opening your invitation…" />;
            break;
        case 'failed':
            content = <BootFailure error={flow.failure} onRetry={() => dispatch({ type: 'boot' })} />;
            break;
        case 'landing':
            content = (
                <Landing preview={flow.preview!} notice={flow.notice} busy={claiming} error={claimError} onStart={start} />
            );
            break;
        case 'connect':
            content = <Connect />;
            break;
        case 'pick':
            content = <PickAccount />;
            break;
        case 'confirm':
            content = <Confirm />;
            break;
        case 'verify':
            content = <VerifyEmail />;
            break;
        case 'create':
            content = <CreateAccount onCreated={onCreated} />;
            break;
        case 'done': {
            const target = handoffRedirectTarget(flow.signInUrl, { allowHttp: ALLOW_HTTP });
            content = (
                <Box>
                    <Typography variant="h5" component="h1" gutterBottom>Your account is ready</Typography>
                    <Typography paragraph>
                        Taking you to your dashboard. Sign in with your e-mail address and the password you just chose.
                    </Typography>
                    {target && <Button variant="contained" href={target}>Go to your dashboard</Button>}
                </Box>
            );
            break;
        }
        case 'returning':
            content = (
                <Box>
                    <Typography variant="h5" component="h1" gutterBottom>Your Curated Planet contact will be in touch</Typography>
                    <Alert severity="info" sx={{ my: 2 }}>{flow.handover}</Alert>
                </Box>
            );
            break;
        case 'released':
            content = (
                <Released link={`${window.location.origin}/#${code}`} onStartAgain={() => dispatch({ type: 'boot' })} />
            );
            break;
        default:
            content = null;
    }

    return (
        <SignupContext.Provider value={context}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
                <SignupHeader onRelease={inWizard ? () => setReleaseOpen(true) : null} />
                <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 5 } }}>
                    <Paper sx={{ p: { xs: 2.5, sm: 4 } }}>
                        {inWizard && <Progress step={flow.step} bound={bound} />}
                        {content}
                    </Paper>
                </Container>
            </Box>
            <ContinueElsewhereDialog open={releaseOpen} onClose={() => setReleaseOpen(false)} onRelease={release} />
        </SignupContext.Provider>
    );
}
